import React, { useState, useEffect } from 'react';
import { Search, Tag, Filter } from 'lucide-react';
import ServiceCard from './ServiceCard';
import { serviceCategories, getServices } from '../../data/services';
import { useNavigate } from 'react-router-dom';
import { trackServiceCategorySelection } from '../../utils/analyticsUtils';

const ServiceCatalog = ({ initialCategory = 'Semua' }) => {
  const navigate = useNavigate();
  
  // Define state variables
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [allServices, setAllServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch all services when component mounts
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const services = await getServices();
        setAllServices(services);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching services:', error);
        setAllServices([]);
        setLoading(false);
      }
    };
    
    fetchServices();
  }, []);

  // Set active category from initialCategory prop
  useEffect(() => {
    setActiveCategory(initialCategory);
  }, [initialCategory]);
  
  // Update URL when category changes, but only if the user explicitly changes it
  // (not when it's set from the URL)
  const updateURL = (category) => {
    if (category === 'Semua') {
      navigate('/services', { replace: true });
    } else {
      navigate(`/services?category=${category}`, { replace: true });
    }
    
    // Force scroll to top of the page when changing categories
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 50);
  };

  // Filter services based on active category and search query
  useEffect(() => {
    let result = [...allServices];
    
    // Filter by category
    if (activeCategory !== 'Semua') {
      result = result.filter(service => service.category === activeCategory);
    }
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(service => 
        service.title.toLowerCase().includes(query) || 
        service.description.toLowerCase().includes(query)
      );
    }
    
    setFilteredServices(result);
  }, [activeCategory, searchQuery, allServices]);

  // Handle category button click
  const handleCategoryClick = (category) => {
    if (category !== activeCategory) {
      setActiveCategory(category);
      updateURL(category);
      
      // Track category selection for analytics
      trackServiceCategorySelection(category);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      {/* Search and Filter Section */}
      <div className="mb-10">
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search Bar */}
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold"
              placeholder="Cari layanan..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          {/* Filter Dropdown (for mobile) */}
          <div className="md:hidden relative">
            <select
              className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-gold focus:border-gold appearance-none bg-white"
              value={activeCategory}
              onChange={(e) => handleCategoryClick(e.target.value)}
            >
              {serviceCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
        
        {/* Category Tabs (desktop) */}
        <div className="hidden md:flex flex-wrap gap-2">
          {serviceCategories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryClick(category)}
              className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === category
                  ? 'bg-gold text-white'
                  : 'bg-cream/30 text-dark-navy hover:bg-cream/50'
              }`}
            >
              <Tag className={`w-3 h-3 mr-1 ${activeCategory === category ? 'text-white' : 'text-dark-navy'}`} />
              {category}
            </button>
          ))}
        </div>
      </div>
      
      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat layanan...</p>
        </div>
      ) : (
        /* Services Grid */
        filteredServices.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Tidak Ada Layanan Ditemukan</h3>
            <p className="text-gray-500">
              Maaf, tidak ada layanan yang sesuai dengan kriteria pencarian Anda.
            </p>
            <button
              onClick={() => {
                setActiveCategory('Semua');
                setSearchQuery('');
                updateURL('Semua');
              }}
              className="mt-4 inline-flex items-center justify-center bg-gold hover:bg-dark-navy text-white font-medium rounded-md px-5 py-2.5 transition-colors"
            >
              Reset Filter
            </button>
          </div>
        )
      )}
    </div>
  );
};

export default ServiceCatalog;